import { assertUncomputedFromBase, fromBaseClone, fromBaseRecord } from './from-base-context';
import type { FromBaseRow } from './from-base-context';
import { readInventoryField } from './inventory-raw-readers';
import { inventoryChildIds, inventoryClosureIds } from './inventory-membership';
import { readItemField } from './item-field-reader';
import { surfaceField } from './from-base-encounter-media';
import { mergeInventoryMetadata } from './inventory-shared-catalog';
import { isSelfStatusOrigin } from '../statuses/status-origin';

export interface BasePatchOp {
    entity_id: string; base_rev: number; table: string; row_id: string;
    op: 'upsert' | 'delete'; cols: FromBaseRow;
    inputs?: FromBaseRow; catalog_inputs?: FromBaseRow; dependency_revs?: Readonly<Record<string, number>>;
}
export interface BasePatchFrame {
    type: 'base_patch'; encounter_id: string | null; ops: BasePatchOp[]; engine?: string; resync?: string[];
    /** JSON wire bytes; receive shares one read-only decode. Adopters own their incoming subtrees. */
    applied?: string;
    /** Post-save revisions of the facets covered by an applied layout delta. */
    base_revs?: Readonly<Record<string, number>>;
}
/** The local page bridge also borrows an already decoded applied plan. */
export type AppliedBasePatchFrame = Omit<BasePatchFrame, 'applied'> & {
    applied?: import('../transport/applied-frame-plan').AppliedFrameInput;
};
/** base.dependency_revs contains ordinary inputs. base.dependency_revs_invalidate_only
 * contains foreign status-origin inputs: both fence/cache-watch revisions, but the latter
 * may trigger only RESYNC, never row operations on this entity's channels. */
export interface BaseEnvelope {
    entity: string; entity_id: string; base_rev: number; engine: string; base: FromBaseRow;
    metadata?: unknown; cache?: { facet: 'inventory' | 'entity'; entity?: string; base_rev: number; engine: string; catalog_version?: string | null; computed: unknown; stage_diagnostics?: readonly string[] } | null;
    catalog_version?: string | null;
    /** Definition closure selected with this envelope; consumed by the catalog owner. */
    catalog_scope?: FromBaseRow;
    viewer_permissions?: { player: boolean; campaign: boolean };
    viewer?: { permissions?: unknown; members?: unknown; inventory?: unknown };
    status_retirement_writer?: { base_rev: number; claimed: boolean };
    cold_compiler?: boolean;
}
export interface BaseBusy {
    entity: string; entity_id: string; engine: string; resync: 'base-busy';
}
const primaryKeys: Readonly<Record<string, string>> = {
    inventory: 'inventory_id', items: 'item_id', encounter_creatures: 'encounter_creature_id', player_characters: 'item_id',
    encounters: 'encounter_id', campaigns: 'campaign_id', locations: 'location_id', statuses: 'status_id', charges: 'charge_id', triggers: 'trigger_id',
    reaction_consents: 'reaction_consent_id',
    pictures: 'picture_id', meshes: 'mesh_id', enchantment_templates: 'enchantment_template_id',
    enchantment_template_enchantments: 'enchantment_id', inventory_key_links: 'inventory_key_link_id',
    inventory_lock_bindings: 'inventory_lock_binding_id', inventory_closure_states: 'inventory_closure_state_id',
    inventory_encounter_creature_links: 'inventory_encounter_creature_link_id', encounter_links: 'encounter_link_id',
    user_choices: 'user_choice_id', action_bars: 'bar_id', action_bar_slots: 'slot_id', action_bar_dividers: 'divider_id',
    action_bar_settings: 'action_bar_setting_id', encounter_creatures_spells_prepared: 'encounter_creature_spell_prepared_id',
    monster_templates: 'monster_template_id', encounter_creature_swarm_states: 'encounter_creature_swarm_state_id',
    encounter_exploration_states: 'encounter_exploration_state_id', location_map_markers: 'location_map_marker_id',
};
const upper = (id: unknown): string => typeof id === 'string' ? id.toUpperCase() : '';
const authorityFields: Readonly<Record<string, readonly string[]>> = {
    player_characters: ['player_character_created_by_id', 'permissioned', 'attached_campaign_id'],
    encounters: ['encounter_created_by_id', 'encounter_master_parent_id', 'encounter_private'],
    locations: ['location_created_by_id', 'location_master_parent_id', 'location_private'],
    campaigns: ['campaign_created_by_id', 'campaign_gm'],
};

function inputHosts(base: FromBaseRow): FromBaseRow[] {
    return [base, ...(Array.isArray(base.inventory) ? base.inventory as FromBaseRow[] : []),
        ...(Array.isArray(base.encounter_creatures_arr) ? (base.encounter_creatures_arr as FromBaseRow[]).flatMap(inputHosts) : [])];
}

/** Snapshot before any sibling facet applies its move or retires its old subtree. */
export function baseInventoryIds(base: FromBaseRow): string[] {
    return inputHosts(base).filter(host => fromBaseRecord(host.inventory)).map(host => rowId('inventory', host));
}

function inventoryPictureIds(node: FromBaseRow): string[] {
    return ['inventory_presentation_pic_id', 'inventory_thumbnail_pic_id']
        .map(key => upper(readInventoryField(fromBaseRecord(node.inventory) ? node : { inventory: node }, key))).filter(Boolean);
}

/** Raw media already on the page or in this receipt; no catalog lookup or tree copy. */
function patchPictures(base: FromBaseRow, ops: readonly BasePatchOp[]): Map<string, FromBaseRow> {
    const sources = [base, ...ops.flatMap(op => op.catalog_inputs ? [op.catalog_inputs] : [])];
    const definitions = sources.flatMap(source => fromBaseRecord(source.catalog) ? Object.values(source.catalog).filter(fromBaseRecord) : []);
    const hosts = inputHosts(base);
    const rows = [...hosts, ...sources, ...definitions, ...ops.flatMap(op => op.inputs ? [op.inputs] : [])]
        .flatMap(source => ['pictures', 'picture_sources', 'media_context'].flatMap(key => Array.isArray(source[key]) ? source[key] as FromBaseRow[] : []));
    rows.push(...ops.filter(op => op.table === 'pictures' && op.op === 'upsert').map(op => op.cols));
    return new Map(rows.map(row => [upper(surfaceField(row, 'picture_id')), row]));
}

/** Raw rows are bare SQL scalars. Inventory still uses the page's canonical bucket reader. */
function rowId(table: string, row: FromBaseRow): string {
    return table === 'inventory' ? upper(readInventoryField(fromBaseRecord(row.inventory) ? row : { inventory: row }, 'inventory_id'))
        : upper(surfaceField(row, primaryKeys[table]!));
}

/** Patch every occurrence of a saved row (a possessor/status owner may also be a nested source). */
function patchExisting(node: unknown, table: string, id: string, cols: FromBaseRow | null, scope = ''): boolean {
    if (!node || typeof node !== 'object') return false;
    let found = false;
    const entries = Array.isArray(node) ? Object.entries(node).reverse() : Object.entries(node);
    for (const [key, child] of entries) {
        if (key === 'catalog' || key === 'item_layer_types') continue;
        const childScope = primaryKeys[key] ? key : scope;
        if (childScope === table && fromBaseRecord(child) && rowId(table, child) === id) {
            found = true;
            if (cols) Object.assign(table === 'inventory' && fromBaseRecord(child.inventory) ? child.inventory : child, fromBaseClone(cols));
            else if (Array.isArray(node)) node.splice(Number(key), 1);
            else delete (node as FromBaseRow)[key];
        } else found = patchExisting(child, table, id, cols, childScope) || found;
    }
    return found;
}

function applyRow(base: FromBaseRow, op: BasePatchOp): void {
    const id = upper(op.row_id), cols = op.op === 'delete' ? null : op.cols;
    if (op.table === 'encounter_creatures' && Array.isArray(base.encounter_creatures_arr) && !cols) {
        base.encounter_creatures_arr = (base.encounter_creatures_arr as FromBaseRow[])
            .filter(host => !fromBaseRecord(host.encounter_creatures) || rowId(op.table, host.encounter_creatures) !== id);
        if (Array.isArray(base.roster_order)) {
            // Retire membership and its SELECT order together. The raw closure
            // holds rows beyond the displayed 50; preserve ties, then backfill.
            const remaining = new Set((base.encounter_creatures_arr as FromBaseRow[]).map(member => upper(member.entity_id)));
            const order = (base.roster_order as string[]).filter(id => remaining.delete(upper(id)));
            base.roster_order = [...order, ...remaining].slice(0, 50);
        }
        return;
    }
    if (op.table === 'items' || op.table === 'item_layer_types') {
        const catalog = fromBaseRecord(base.catalog) ? base.catalog : (base.catalog = {}) as FromBaseRow;
        if (op.table === 'items' && !cols) {
            delete catalog[id];
            base.missing_definition_ids = [...new Set([...(base.missing_definition_ids as string[] ?? []), id])];
        } else if (cols) {
            const definition = fromBaseRecord(catalog[id]) ? catalog[id] : (catalog[id] = { items: { item_id: id }, item_layer_types: {} });
            if (op.inputs) Object.assign(definition, fromBaseClone(op.inputs));
            (definition as FromBaseRow)[op.table] = fromBaseClone(cols);
            base.missing_definition_ids = (base.missing_definition_ids as string[] ?? []).filter(value => upper(value) !== id);
        }
        return;
    }
    if (!primaryKeys[op.table]) throw new Error(`unported:base-patch-table:${op.table}`);
    if (op.table === 'statuses') {
        const hosts = inputHosts(base);
        for (const host of hosts) {
            const hostId = upper(host.entity_id) || rowId('inventory', host);
            const statuses = fromBaseRecord(host.statuses) ? host.statuses : (host.statuses = { target: [], caster: [] }) as FromBaseRow;
            for (const mode of ['target', 'caster']) {
                const rows = Array.isArray(statuses[mode]) ? statuses[mode] as FromBaseRow[] : (statuses[mode] = []) as FromBaseRow[];
                const index = rows.findIndex(row => rowId('statuses', row) === id);
                const ownerKey = `status_${mode}_id`;
                const belongs = cols && (upper(surfaceField(cols, ownerKey)) === hostId || (!(ownerKey in cols) && index >= 0));
                if (belongs) {
                    if (index >= 0) Object.assign(rows[index]!, fromBaseClone(cols));
                    else rows.push(fromBaseClone(cols));
                } else if (index >= 0) rows.splice(index, 1);
            }
        }
        return;
    }
    if (op.table === 'encounter_creatures') for (const host of inputHosts(base))
        for (const row of Array.isArray(host.status_owners) ? host.status_owners as FromBaseRow[] : [])
            if (cols && rowId(op.table, row) === id) Object.assign(row, fromBaseClone(cols));
    if (patchExisting(base, op.table, id, cols)) return;
    if (!cols) return;
    if (op.table === 'charges' || ['pictures', 'meshes', 'reaction_consents'].includes(op.table) || op.table.startsWith('inventory_')) {
        const fields: Record<string, string[]> = {
            charges: ['charge_holder'], pictures: ['picture_attached_id'], meshes: ['mesh_attached_id'],
            reaction_consents: ['reaction_consent_creature_id'],
            inventory_key_links: ['inventory_key_link_key_id', 'inventory_key_link_lock_id'],
            inventory_lock_bindings: ['inventory_lock_binding_lock_id', 'inventory_lock_binding_secured_id'],
            inventory_closure_states: ['inventory_closure_state_closure_id'],
            inventory_encounter_creature_links: ['inventory_encounter_creature_link_inventory_id'],
        };
        for (const host of inputHosts(base)) {
            const hostId = upper(host.entity_id) || rowId('inventory', host);
            if (!(fields[op.table] ?? []).some(field => upper(surfaceField(cols, field)) === hostId)) continue;
            const rows = Array.isArray(host[op.table]) ? host[op.table] : (host[op.table] = []);
            (rows as FromBaseRow[]).push(fromBaseClone(cols));
        }
        return;
    }
    if (['encounter_creatures', 'player_characters', 'encounters', 'campaigns', 'locations'].includes(op.table)
        && id !== upper(base.entity_id)) return;
    const rows = base[op.table];
    if (Array.isArray(rows)) rows.push(fromBaseClone(cols));
    else if (['encounters', 'campaigns', 'locations', 'encounter_creatures', 'player_characters'].includes(op.table)) base[op.table] = fromBaseClone(cols);
    else throw new Error(`unported:base-patch-insert:${op.table}`);
}

/** Move held nodes with all their saved inputs; fresh inserts start on the same op lane.
 * Repartition once per batch so container children follow their parent in either op order. */
function applyInventoryRows(base: FromBaseRow, ops: readonly BasePatchOp[]): void {
    const writes = ops.filter(op => op.table === 'inventory');
    if (!writes.length) return;
    const hosts = inputHosts(base).filter(host => Array.isArray(host.inventory));
    const nodes = new Map(hosts.flatMap(host => (host.inventory as FromBaseRow[]).map(node => [rowId('inventory', node), node] as const)));
    const removed = new Set<string>();
    for (const op of writes) {
        const id = upper(op.row_id), previous = nodes.get(id);
        if (op.op === 'delete') removed.add(id);
        else {
            const node = previous ?? { inventory: { inventory_id: id }, base_rev: 0,
                statuses: { target: [], caster: [] }, charges: [], pictures: [], meshes: [],
                inventory_lock_bindings: [], inventory_key_links: [], inventory_closure_states: [], inventory_encounter_creature_links: [] };
            Object.assign(node.inventory as FromBaseRow, fromBaseClone(op.cols));
            nodes.set(id, node);
        }
        for (const host of hosts) {
            patchExisting(host.possessor, 'inventory', id, op.op === 'delete' ? null : op.cols);
            patchExisting(host.status_origin_inventory_sources, 'inventory', id, op.op === 'delete' ? null : op.cols);
        }
    }
    const children = inventoryChildIds(nodes.values());
    const retire = [...removed];
    for (let at = 0; at < retire.length; at++) for (const id of children.get(retire[at]!) ?? [])
        if (!removed.has(id)) { removed.add(id); retire.push(id); }
    for (const host of hosts) {
        const seen = inventoryClosureIds(children, upper(host.entity_id) || rowId('inventory', host), removed);
        // Existing nodes already carry the column updates. Repartition only owners whose membership changed;
        // sorting an untouched holder here changes its compile witness and re-revs it for someone else's throw.
        const previous = host.inventory as FromBaseRow[];
        if (previous.length !== seen.size || previous.some(node => !seen.has(rowId('inventory', node))
            || node !== nodes.get(rowId('inventory', node)))) host.inventory = [...seen].map(id => nodes.get(id)!);
    }
}

/** Reindex saved status source identities from rows already held in this realm. */
export function refreshStatusInputs(base: FromBaseRow, ops: readonly BasePatchOp[] = [], peerBases: readonly FromBaseRow[] = []): void {
    const hosts = inputHosts(base), owners = new Map<string, FromBaseRow>();
    const sources = [...peerBases.flatMap(inputHosts), ...hosts];
    const connections = new Map<string, FromBaseRow>();
    const targets = { inventory: new Map<string, FromBaseRow>(), encounter_creatures: new Map<string, FromBaseRow>() };
    for (const host of sources) {
        for (const row of Array.isArray(host.status_owners) ? host.status_owners as FromBaseRow[] : []) owners.set(rowId('encounter_creatures', row), row);
        if (fromBaseRecord(host.encounter_creatures)) owners.set(rowId('encounter_creatures', host.encounter_creatures), host.encounter_creatures);
        for (const status of Array.isArray(host.status_connection_rows) ? host.status_connection_rows.filter(fromBaseRecord) : [])
            connections.set(rowId('statuses', status), status);
        const refs = fromBaseRecord(host.status_target_references) ? host.status_target_references : {};
        for (const table of ['inventory', 'encounter_creatures'] as const) {
            for (const row of Array.isArray(refs[table]) ? refs[table].filter(fromBaseRecord) : []) targets[table].set(rowId(table, row), row);
        }
    }
    for (const host of sources) for (const table of ['inventory', 'encounter_creatures'] as const)
        if (fromBaseRecord(host[table])) targets[table].set(rowId(table, host[table]), host[table]);
    // Current held rows and this receipt supersede the earlier SELECT connection closure.
    for (const host of sources) if (fromBaseRecord(host.statuses)) for (const mode of ['target', 'caster'])
        for (const status of Array.isArray(host.statuses[mode]) ? host.statuses[mode].filter(fromBaseRecord) : [])
            connections.set(rowId('statuses', status), status);
    for (const op of ops) if (op.table === 'statuses') {
        const id = upper(op.row_id);
        if (op.op === 'delete' || Number(surfaceField(op.cols, 'status_deleted') ?? 0) !== 0) connections.delete(id);
        else connections.set(id, Object.assign(connections.get(id) ?? {}, op.cols));
    }
    for (const host of hosts) {
        if (!fromBaseRecord(host.statuses)) continue;
        const rows = ['target', 'caster'].flatMap(mode => Array.isArray((host.statuses as FromBaseRow)[mode]) ? (host.statuses as FromBaseRow)[mode] as FromBaseRow[] : []);
        const connectionIds = new Set(rows.map(row => upper(surfaceField(row, 'status_connected_id'))).filter(Boolean));
        const peers = [...connections.values()].filter(row => connectionIds.has(upper(surfaceField(row, 'status_connected_id')))
            && Number(surfaceField(row, 'status_deleted') ?? 0) === 0);
        host.status_connection_rows = peers;
        const targetIds = new Set(peers.map(row => upper(surfaceField(row, 'status_target_id'))).filter(id => id && id !== upper(host.entity_id)));
        host.status_target_references = Object.fromEntries((['inventory', 'encounter_creatures'] as const)
            .map(table => [table, [...targets[table]].filter(([id]) => targetIds.has(id)).map(([, row]) => row)]));
        if (Array.isArray(host.status_owners)) {
            const ids = new Set(rows.map(row => upper(surfaceField(row, 'status_caster_id'))));
            host.status_owners = [...owners].filter(([id]) => ids.has(id)).map(([, row]) => fromBaseClone(row))
                .sort((a, b) => Number(surfaceField(a, 'creature_number') ?? 0) - Number(surfaceField(b, 'creature_number') ?? 0)
                    || rowId('encounter_creatures', a).localeCompare(rowId('encounter_creatures', b)));
        }
        if (!fromBaseRecord(host.status_origin_references)) continue;
        const refs = host.status_origin_references;
        const ids = [...new Set(rows.map(row => upper(surfaceField(row, 'status_origin_id'))).filter(Boolean))];
        refs.covered_ids = ids;
        for (const table of ['items', 'player_characters', 'encounter_creatures', 'inventory', 'encounters', 'locations']) {
            const key = primaryKeys[table]!;
            const held = new Map((Array.isArray(refs[table]) ? refs[table] as FromBaseRow[] : []).map(row => [rowId(table, row), row]));
            for (const source of hosts) if (fromBaseRecord(source[table])) held.set(rowId(table, source[table] as FromBaseRow), source[table] as FromBaseRow);
            if (table === 'items' && fromBaseRecord(base.catalog)) for (const id of ids) {
                const definition = base.catalog[id];
                if (fromBaseRecord(definition) && fromBaseRecord(definition.items)) held.set(id, definition.items);
            }
            refs[table] = ids.filter(id => held.has(id)).map(id => ({ [key]: surfaceField(held.get(id)!, key) }));
        }
    }
}

/** Entire entity batch is atomic. Multiple ops at one revision compile once; a gap changes nothing. */
export function applyBasePatch(envelope: BaseEnvelope, ops: readonly BasePatchOp[], priorInventoryIds?: ReadonlySet<string>): 'applied' | 'duplicate' | 'gap' | 'resync' {
    if (ops.length === 0) return 'duplicate';
    const revisions = new Set(ops.map(op => op.base_rev));
    if (revisions.size !== 1 || ops.some(op => upper(op.entity_id) !== upper(envelope.entity_id))) throw new TypeError('Mixed base patch entity/revision');
    const revision = ops[0]!.base_rev;
    if (!Number.isSafeInteger(revision) || revision < 0) throw new TypeError('Invalid base revision');
    if (revision <= envelope.base_rev) return 'duplicate';
    if (revision !== envelope.base_rev + 1) return 'gap';
    // Definitions carried by this save are available to every guard in the atomic batch.
    const definitions = new Set(Object.keys(fromBaseRecord(envelope.base.catalog) ? envelope.base.catalog : {}).map(upper));
    for (const op of ops) if (op.table === 'items') {
        if (op.op === 'delete') definitions.delete(upper(op.row_id));
        else definitions.add(upper(op.row_id));
    }
    for (const op of ops) {
        if (envelope.entity === 'inventory') continue;
        const hosts = inputHosts(envelope.base);
        if (op.op === 'upsert' && authorityFields[op.table]) {
            const existing = hosts.find(host => fromBaseRecord(host[op.table]) && rowId(op.table, host[op.table] as FromBaseRow) === upper(op.row_id));
            const saved = existing?.[op.table] as FromBaseRow | undefined;
            if (saved && authorityFields[op.table]!.some(key => key in op.cols
                && String(surfaceField(saved, key) ?? '') !== String(surfaceField(op.cols, key) ?? ''))) return 'resync';
        }
        if (op.table === 'encounter_creatures' && op.op === 'upsert') {
            const existing = hosts.find(host => fromBaseRecord(host.encounter_creatures) && rowId(op.table, host.encounter_creatures) === upper(op.row_id));
            if (!existing) return 'resync';
            const row = existing.encounter_creatures as FromBaseRow;
            if (['item_id', 'encounter_id', 'controller_encounter_creature_id', 'encounter_creature_created_by_id', 'creature_number']
                .some(key => key in op.cols && String(readItemField(row, key) ?? '') !== String(readItemField(op.cols, key) ?? ''))) return 'resync';
        }
        if (['user_choices', 'action_bars', 'action_bar_slots', 'action_bar_dividers', 'action_bar_settings',
            'encounter_creatures_spells_prepared', 'encounter_creature_triggers', 'reaction_consents', 'encounter_links', 'location_map_markers', 'triggers', 'layers', 'item_layers',
            'monster_templates', 'monster_template_layers', 'enchantment_templates', 'enchantment_template_enchantments', 'campaign_house_rules'].includes(op.table)) return 'resync';
        if (op.op === 'upsert' && ['player_characters', 'encounter_creatures'].includes(op.table)) {
            const existing = hosts.find(host => fromBaseRecord(host[op.table]) && rowId(op.table, host[op.table] as FromBaseRow) === upper(op.row_id));
            const saved = existing?.[op.table] as FromBaseRow | undefined;
            for (const key of Object.keys(op.cols)) {
                const value = surfaceField(op.cols, key);
                if (saved && String(surfaceField(saved, key) ?? '') === String(value ?? '')) continue;
                if (typeof value === 'string' && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value)
                    && !key.endsWith('_by_id') && !['encounter_id', 'encounter_creature_id', 'item_id', 'permissioned', 'combat_id',
                        'attached_campaign_id', 'controller_encounter_creature_id', 'movement_last_operation_id', 'encounter_creature_concentrating_status_id'].includes(key)
                    && !definitions.has(upper(value))) return 'resync';
            }
        }
    }
    const missing = new Set([...(Array.isArray(envelope.base.missing_definition_ids) ? envelope.base.missing_definition_ids.map(upper) : []),
        ...ops.flatMap(op => Array.isArray(op.catalog_inputs?.missing_definition_ids) ? op.catalog_inputs.missing_definition_ids.map(upper) : [])]);
    // Missing references still recover; a definition delivered in these ops needs no catalog GET.
    for (const op of ops) if (op.op === 'upsert' && op.table === 'statuses')
        for (const field of ['status_item_id', 'status_origin_id']) {
            const reference = upper(readItemField(op.cols, field));
            if (field === 'status_origin_id' && isSelfStatusOrigin(upper(op.row_id), reference)) continue;
            if (reference && !definitions.has(reference) && !missing.has(reference)) return 'resync';
        }
    const inventoryUpserts = ops.filter(op => op.op === 'upsert' && op.table === 'inventory');
    const heldInventory = inventoryUpserts.length ? new Set(baseInventoryIds(envelope.base)) : new Set<string>();
    const pictures = inventoryUpserts.length ? patchPictures(envelope.base, ops) : new Map<string, FromBaseRow>();
    const pictureLookups = new Set([...inputHosts(envelope.base), ...ops.flatMap(op => op.catalog_inputs ? [op.catalog_inputs] : [])]
        .flatMap(source => [...Array.isArray(source.picture_lookup_ids) ? source.picture_lookup_ids.map(upper) : [],
            // A pointer already used by a held node was covered by that node's SELECT closure.
            ...fromBaseRecord(source.inventory) ? inventoryPictureIds(source) : []]));
    for (const op of inventoryUpserts) {
        const id = upper(op.row_id);
        // A contents facet already holds its own root in possessor, outside its inventory nodes.
        heldInventory.add(upper(envelope.entity_id));
        const definition = upper(readInventoryField({ inventory: op.cols }, 'inventory_item_id'));
        const moved = !definition && Boolean(readInventoryField({ inventory: op.cols }, 'possess_id'));
        // A known row acquired from outside this SELECT closure needs its real descendants,
        // statuses, charges and links. A holder-only UPDATE also proves this is not a fresh
        // INSERT (which supplies inventory_item_id), even without a sibling source facet.
        if (!heldInventory.has(id) && (moved || priorInventoryIds?.has(id))) return 'resync';
        if (inventoryPictureIds(op.cols).some(id => !pictures.has(id) && !pictureLookups.has(id))) return 'resync';
        if (definition && !definitions.has(definition)) return 'resync';
        for (const host of inputHosts(envelope.base)) {
            if (!Array.isArray(host.inventory)) continue;
            const nodes = host.inventory as FromBaseRow[];
            const previous = nodes.find(node => rowId('inventory', node) === id);
            // Renaming/replacing a saved item changes native SQL collation order.
            if (previous && (Array.isArray(host.inventory_order) || Array.isArray(host.floor_order))
                && ['inventory_name', 'inventory_item_id'].some(field => field in op.cols && readInventoryField(previous, field)
                    !== readInventoryField({ inventory: op.cols }, field))) return 'resync';
        }
    }
    applyBaseRowChanges(envelope, ops);
    return 'applied';
}

/** Apply local, already-decided saves with a complete SELECT closure. Network patches
 * use applyBasePatch, whose resync guards also cover changes to that closure.
 * inPlace retains an already-projected local operation's held saved rows. */
export function applyBaseRowChanges(envelope: BaseEnvelope, ops: readonly BasePatchOp[], inPlace = false): void {
    if (!ops.length) return;
    const revision = ops[0]!.base_rev;
    // patchExisting never enters authored catalogs/layer buckets. Share those
    // immutable inputs rather than copying megabytes for a saved-row patch.
    let next = inPlace ? envelope.base : fromBaseClone(envelope.base, new Set(['catalog', 'item_layer_types']));
    for (const op of ops) if (op.catalog_inputs) {
        assertUncomputedFromBase(op.catalog_inputs);
        next = mergeInventoryMetadata(next, op.catalog_inputs);
    }
    if (ops.some(op => op.table === 'items' || op.table === 'item_layer_types') && fromBaseRecord(next.catalog)) {
        next.catalog = { ...next.catalog };
        for (const op of ops) if (fromBaseRecord((next.catalog as FromBaseRow)[upper(op.row_id)]))
            (next.catalog as FromBaseRow)[upper(op.row_id)] = { ...(next.catalog as FromBaseRow)[upper(op.row_id)] as FromBaseRow };
    }
    applyInventoryRows(next, ops);
    for (const op of ops) {
        if (op.op !== 'delete' && op.op !== 'upsert') throw new TypeError('Invalid base operation');
        assertUncomputedFromBase(op.cols);
        if (op.inputs) assertUncomputedFromBase(op.inputs);
        // The first flip owns the possessor's inventory facet. PC choices/action bars and encounter
        // geometry belong to their later facets, although the shared relay already carries those rows.
        if (envelope.entity === 'inventory' && ['user_choices', 'action_bars', 'action_bar_slots', 'action_bar_dividers',
            'action_bar_settings', 'encounter_creatures_spells_prepared', 'encounter_creature_triggers', 'encounter_creature_swarm_states',
            'encounter_exploration_states', 'location_map_markers'].includes(op.table)) continue;
        if (op.table !== 'inventory') applyRow(next, op);
        // Combat identities are saved relationships, never authored items. The
        // fresh SELECT closure records their known-empty shell lookup too.
        const combatId = upper(surfaceField(op.cols, op.table === 'encounter_creatures' ? 'combat_id' : 'active_combat_id'));
        if (op.op === 'upsert' && ['encounter_creatures', 'encounters'].includes(op.table) && combatId
            && fromBaseRecord(next.inventory_metadata) && fromBaseRecord(next.inventory_metadata.shellRows))
            next.inventory_metadata.shellRows[combatId] ??= null;
    }
    const touchedStatuses = new Set(ops.filter(op => op.table === 'statuses' && op.op === 'upsert').map(op => upper(op.row_id)));
    if (ops.some(op => op.table === 'statuses')) {
        // SQL orders newest-touched first, then status_id DESC. Its automatic
        // timestamp is absent from insert/update echoes; preserve the held order
        // behind this batch rather than comparing missing or stale timestamps.
        for (const host of inputHosts(next)) if (fromBaseRecord(host.statuses)) for (const mode of ['target', 'caster']) {
            const rows = host.statuses[mode];
            if (Array.isArray(rows)) (rows as FromBaseRow[]).sort((a, b) =>
                Number(touchedStatuses.has(rowId('statuses', b))) - Number(touchedStatuses.has(rowId('statuses', a)))
                || (touchedStatuses.has(rowId('statuses', a)) ? rowId('statuses', b).localeCompare(rowId('statuses', a)) : 0));
        }
        refreshStatusInputs(next, ops);
    }
    if (ops.some(op => op.table === 'inventory' && op.op === 'upsert')) {
        const pictures = patchPictures(next, ops);
        const upserts = new Set(ops.filter(op => op.table === 'inventory' && op.op === 'upsert').map(op => upper(op.row_id)));
        const hosts = inputHosts(next);
        for (const host of hosts) if (Array.isArray(host.inventory)
            && (host === next || 'picture_lookup_ids' in host || 'picture_sources' in host)) {
            // Members without their own picture closure inherit the encounter's shared inputs.
            const nodes = host === next ? hosts.filter(node => fromBaseRecord(node.inventory)) : host.inventory as FromBaseRow[];
            const ids = [...new Set(nodes.filter(node => upserts.has(rowId('inventory', node))).flatMap(inventoryPictureIds))];
            host.picture_lookup_ids = [...new Set([...(Array.isArray(host.picture_lookup_ids) ? host.picture_lookup_ids as string[] : []), ...ids])];
            const sources = new Map((Array.isArray(host.picture_sources) ? host.picture_sources as FromBaseRow[] : [])
                .map(row => [upper(surfaceField(row, 'picture_id')), row]));
            for (const id of ids) if (pictures.has(id)) sources.set(id, pictures.get(id)!);
            host.picture_sources = [...sources.values()];
        }
    }
    const inventoryChanged = ops.some(op => op.table === 'inventory');
    for (const host of inputHosts(next)) if ((!inPlace || inventoryChanged) && Array.isArray(host.inventory_order) && Array.isArray(host.inventory)) {
        // Encounter SELECT order covers floor and member inventories; its member compiler selects from this shared witness list.
        const inventories = host === next && envelope.entity === 'encounter'
            ? inputHosts(next).flatMap(owner => Array.isArray(owner.inventory) ? owner.inventory as FromBaseRow[] : [])
            : host.inventory as FromBaseRow[];
        const remaining = new Map(inventories.map(node => [rowId('inventory', node), node]));
        // A holder change preserves global name order; departed rows leave both sources.
        const ordered = (host.inventory_order as FromBaseRow[]).filter(row => {
            const node = remaining.get(rowId('inventory', row));
            if (!node) return false;
            const saved = fromBaseRecord(row.inventory) ? row.inventory : row;
            saved.possess_id = readInventoryField(node, 'possess_id');
            return true;
        });
        const ids = new Set(ordered.map(row => rowId('inventory', row)));
        const catalog = fromBaseRecord(next.catalog) ? next.catalog : {};
        const name = (row: FromBaseRow): string => String(readInventoryField(fromBaseRecord(row.inventory) ? row : { inventory: row }, 'inventory_name')
            || readItemField(row, 'item_name') || 'Unknown');
        for (const [id, node] of remaining) if (!ids.has(id)) {
            const row = Object.fromEntries(['inventory_id', 'inventory_item_id', 'possess_id', 'inventory_name']
                .map(key => [key, readInventoryField(node, key)]));
            row.item_name = readItemField(catalog[upper(row.inventory_item_id)] as FromBaseRow ?? {}, 'item_name');
            const index = ordered.findIndex(held => name(row).localeCompare(name(held), 'en', { sensitivity: 'base' }) < 0
                || (name(row).localeCompare(name(held), 'en', { sensitivity: 'base' }) === 0 && id < rowId('inventory', held)));
            ordered.splice(index < 0 ? ordered.length : index, 0, row);
        }
        host.inventory_order = ordered;
    }
    // floor_order is the encounter's saved SELECT order of the rows it holds directly: a deleted or departed floor row
    // leaves it, as from inventory_order (genocorpse: a GM floor delete left its id and every later compile refused).
    if ((!inPlace || inventoryChanged) && Array.isArray(next.floor_order) && Array.isArray(next.inventory)) {
        const floor = new Set((next.inventory as FromBaseRow[]).filter(node => upper(readInventoryField(node, 'possess_id')) === upper(next.entity_id))
            .map(node => rowId('inventory', node)));
        const order = Array.isArray(next.inventory_order) ? (next.inventory_order as FromBaseRow[]).map(row => rowId('inventory', row))
            : [...floor];
        next.floor_order = order.filter(id => floor.has(upper(id)));
        if (ops.some(op => op.table === 'inventory') && Array.isArray(next.media_context)) {
            const selected = (base: FromBaseRow): Set<string> => new Set((Array.isArray(base.inventory) ? base.inventory as FromBaseRow[] : [])
                .flatMap(node => ['inventory_presentation_pic_id', 'inventory_thumbnail_pic_id'].map(key => upper(readInventoryField(node, key)))).filter(Boolean));
            const previous = selected(envelope.base), current = selected(next);
            const pictures = patchPictures(next, ops);
            const context = (next.media_context as FromBaseRow[]).filter(row => !previous.has(upper(surfaceField(row, 'picture_id'))) || current.has(upper(surfaceField(row, 'picture_id'))));
            const held = new Set(context.map(row => upper(surfaceField(row, 'picture_id'))));
            for (const id of current) if (!held.has(id) && pictures.has(id)) context.push(pictures.get(id)!);
            next.media_context = context;
        }
    }
    const revisions = Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})) as Record<string, number>;
    for (const host of inputHosts(next)) {
        const id = upper(host.entity_id) || rowId('inventory', host);
        if (id in revisions && 'base_rev' in host) host.base_rev = revisions[id];
    }
    if (fromBaseRecord(next.dependency_revs)) {
        for (const [id, rev] of Object.entries(revisions))
            if (id in next.dependency_revs) next.dependency_revs[id] = rev;
        next.dependency_revs[upper(envelope.entity_id)] = revision;
        if (ops.some(op => op.table === 'inventory')) {
            const current = new Set(inputHosts(next).filter(host => fromBaseRecord(host.inventory)).map(host => rowId('inventory', host)));
            for (const host of inputHosts(envelope.base)) if (fromBaseRecord(host.inventory)) {
                const id = rowId('inventory', host);
                if (!current.has(id)) delete next.dependency_revs[id];
            }
            for (const id of current) if (!(id in next.dependency_revs)) next.dependency_revs[id] = revisions[id] ?? 0;
        }
    }
    next.base_rev = revision;
    // The catalog scope is keyed by the revision a base holds (`entity~id:rev`). A patched base
    // asks for the revision it now holds; the superseded one is refused by the endpoint by design.
    if (typeof next.catalog_ref === 'string') next.catalog_ref = next.catalog_ref.replace(/:\d+$/, `:${revision}`);
    envelope.base = next; envelope.base_rev = revision; envelope.cache = null;
}
